import React, { useState, memo } from "react";
import { useForm, SubmitHandler, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  UserProfile,
  UserProfileAchievements,
  useRestApiProvider,
} from "@jugl-web/rest-api";
import { ReactComponent as BinIcon } from "@web-src/images/icons/delete.svg";
import format from "date-fns/format";
import { isoToLocalDate } from "@web-src/utils/helper";
import { AvatarSelect } from "@jugl-web/ui-components/web/AvatarSelect";
import { useTranslations } from "@jugl-web/utils";
import { DatePicker } from "@jugl-web/ui-components/cross-platform/DatePicker";
import {
  InteractiveContainer,
  Popover,
  Button,
  Divider,
  LoadingAnimation,
  TextField,
  FormControlLabel,
  ConfirmationPopup,
} from "@jugl-web/ui-components/cross-platform";
import { Accordion } from "@jugl-web/ui-components/cross-platform/Accordion";
import { ReactComponent as AwardIcon } from "./assets/award.svg";
import { useProfileUpdateToast } from "../../hooks/useProfileUpdateToast";
import { DateButton } from "../DateButton/DateButton";

type Inputs = {
  name_achievement: string;
  date: Date | undefined;
  reference: string;
};

const schema = yup.object().shape({
  name_achievement: yup.string().required(),
  date: yup.date().required(),
  reference: yup.string().required(),
});

const EditAchievementsFormContainer = memo(() => {
  const { usersApi } = useRestApiProvider();
  const { data } = usersApi.useGetUserProfileQuery({});
  return <EditAchievementsFormComponent {...{ data }} />;
});

const EditAchievementsFormComponent: React.FC<{
  data: UserProfile | undefined;
}> = ({ data }) => {
  const { t } = useTranslations();
  return (
    <div className="w-1/2">
      {!data ? (
        <LoadingAnimation />
      ) : data.achievements.length === 0 ? (
        <div className="pt-10">
          <span className="pl-5 font-medium">
            {t({
              id: "achievements-form-page.add-achievement",
              defaultMessage: "Add Achievement",
            })}
          </span>
          <EditAchievementsForm type="add" />
        </div>
      ) : (
        <EditAchievementsList {...{ data }} />
      )}
    </div>
  );
};

const EditAchievementsList: React.FC<{ data: UserProfile }> = ({ data }) => {
  const [displayForm, setDisplayForm] = useState(false);
  const { t } = useTranslations();
  return (
    <div className="pt-5">
      <span className="pl-5 font-medium">
        {t({
          id: "achievements-form-page.add-achievement",
          defaultMessage: "Add Achievement",
        })}
      </span>
      {data.achievements.map((el, idx) => (
        <>
          <EditAchievementsListItem key={el.id} data={el} />
          {data.achievements.length - 1 > idx && <Divider />}
        </>
      ))}
      {!displayForm && (
        <Button
          className="ml-5 mt-5 mb-10 w-1/2"
          variant="outlined"
          onClick={() => setDisplayForm(true)}
        >
          {t({
            id: "form-controls.add-new-achievement.button",
            defaultMessage: "Add New Achievement",
          })}
        </Button>
      )}
      {displayForm && (
        <>
          <span className="ml-5 font-medium">
            {t({
              id: "achievements-form-page.add-new-achievement",
              defaultMessage: "Add New Achievement",
            })}
          </span>
          <EditAchievementsForm
            type="add"
            onSuccess={() => setDisplayForm(false)}
          />
        </>
      )}
    </div>
  );
};

const EditAchievementsListItem: React.FC<{ data: UserProfileAchievements }> = ({
  data,
}) => {
  const [openModal, setOpenModal] = useState(false);
  const { usersApi } = useRestApiProvider();
  const [deleteAchievement, { isLoading }] =
    usersApi.useDeleteUserProfileSectionMutation();
  const { showProfileUpdatedSnackbar } = useProfileUpdateToast();
  const { t } = useTranslations();

  const handleDeleteAchievement = async () => {
    const res = await deleteAchievement({
      type: "achievement",
      item_id: data.id,
    });
    if ("data" in res) {
      showProfileUpdatedSnackbar();
      setOpenModal(false);
    }
  };

  return (
    <>
      <Accordion
        variant="web"
        title={
          <div className="flex w-full items-center gap-5 px-5 py-3">
            {data.img ? (
              <img
                src={data.img}
                className="h-9 w-9 rounded-full"
                alt="Achievement"
              />
            ) : (
              <div className="flex h-9 w-9 items-center justify-center rounded-full bg-[rgba(238,90,138,0.1)]">
                <AwardIcon />
              </div>
            )}
            <div className="flex grow flex-col gap-1">
              <span>{data.title}</span>
              {data.date && (
                <span className="text-grey text-sm">
                  {format(isoToLocalDate(data.date), "dd/MMMM/yyyy")}
                </span>
              )}
            </div>
            <Button
              color="tertiary"
              className="text-sm font-medium uppercase"
              isDisabled={isLoading}
              onClick={(e) => {
                e.stopPropagation();
                setOpenModal(true);
              }}
              variant="text"
              iconStart={<BinIcon />}
            >
              {t({
                id: "common.delete",
                defaultMessage: "Delete",
              })}
            </Button>
          </div>
        }
        className="flex-row-reverse px-4"
      >
        {({ onClose }) => (
          <div className="px-4 py-2">
            <EditAchievementsForm
              {...{ data }}
              type="update"
              onSuccess={onClose}
            />
          </div>
        )}
      </Accordion>
      {openModal && (
        <ConfirmationPopup
          isOpen={openModal}
          onRequestClose={() => setOpenModal(false)}
          hasCancelButton
          message={t({
            id: "achievements-form-page.achievements-information-removal-confirmation",
            defaultMessage:
              "Do you really want to remove Achievements information?",
          })}
          variant="web"
          title={t({
            id: "delete-modal.delete-confirmation-title",
            defaultMessage: "Delete Confirmation",
          })}
          buttons={[
            {
              label: t({
                id: "common.delete",
                defaultMessage: "Delete",
              }),
              color: "tertiary",
              onClick: handleDeleteAchievement,
              isDisabled: isLoading,
            },
          ]}
        />
      )}
    </>
  );
};

const EditAchievementsForm: React.FC<{
  data?: UserProfileAchievements;
  type: "add" | "update";
  onSuccess?: () => void;
}> = ({ data, type, onSuccess }) => {
  const { usersApi } = useRestApiProvider();
  const [addAchievement, { isLoading }] =
    usersApi.useAddUserProfileSectionMutation();
  const [selectedAvatar, setSelectedAvatar] = useState<File>();
  const [updateAchievement, { isLoading: isLoadingUpdate }] =
    usersApi.useUpdateUserProfileSectionMutation();
  const { showProfileUpdatedSnackbar } = useProfileUpdateToast();
  const { t } = useTranslations();
  const {
    register,
    handleSubmit,
    control,
    formState: { isValid, isDirty },
  } = useForm<Inputs>({
    resolver: yupResolver(schema),
    values: {
      name_achievement: data?.title || "",
      date: data?.date ? new Date(data.date) : undefined,
      reference: data?.ref || "",
    },
  });

  const onSubmit: SubmitHandler<Inputs> = async (params) => {
    const {
      name_achievement: NameAchievement,
      date,
      reference,
    } = { ...params };
    const dataForm = new FormData();
    dataForm.set("type", "achievement");
    dataForm.set("title", NameAchievement);
    dataForm.set("ref", reference);
    dataForm.set("date", date?.toISOString() || "");
    if (selectedAvatar) dataForm.set("display_pic_file", selectedAvatar);
    let res;
    if (type === "add") {
      res = await addAchievement({ data: dataForm });
    } else {
      res = await updateAchievement({
        item_id: data?.id,
        data: dataForm,
      });
    }
    if ("data" in res) {
      showProfileUpdatedSnackbar();
      onSuccess?.();
    }
  };

  return (
    <div className="px-10 pb-10 pt-5">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-5">
          <AvatarSelect
            onSave={setSelectedAvatar}
            value={selectedAvatar}
            defaultSrc={data?.img || undefined}
            placeholder={<AwardIcon className="h-10 w-10" />}
          />
        </div>
        <div className="flex w-full gap-[70px]">
          <div className="flex w-1/2 flex-col gap-12">
            <TextField
              isRequired
              label={t({
                id: "form-controls.achievement-name.label",
                defaultMessage: "Name of the Achievement",
              })}
              isFullWidth
              placeholder={t({
                id: "form-controls.achievement-name.placeholder",
                defaultMessage: "Name of the Achievement",
              })}
              {...register("name_achievement")}
            />
            <TextField
              isRequired
              label={t({
                id: "form-controls.reference-link.label",
                defaultMessage: "Paste Reference Link",
              })}
              isFullWidth
              placeholder={t({
                id: "form-controls.reference-link.placeholder",
                defaultMessage: "Paste Reference Link",
              })}
              {...register("reference")}
            />
            <Button
              isDisabled={!isValid || isLoading || isLoadingUpdate || !isDirty}
              fullWidth
              variant="contained"
              type="submit"
            >
              {t({
                id: "common.save",
                defaultMessage: "Save",
              })}
            </Button>
          </div>
          <div className="w-1/2">
            <Controller
              name="date"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Popover
                  className="w-[340px]"
                  placement="bottom-start"
                  renderTrigger={({ Trigger, triggerRef }) => (
                    <Trigger ref={triggerRef} as={InteractiveContainer}>
                      <FormControlLabel isRequired className="ml-2.5">
                        {t({
                          id: "form-controls.achievement-date.label",
                          defaultMessage: "Achievement Date",
                        })}
                      </FormControlLabel>
                      <DateButton date={value} />
                    </Trigger>
                  )}
                >
                  {({ onClose }) => (
                    <DatePicker
                      onSubmit={onChange}
                      initialDate={value}
                      onClose={onClose}
                      className="p-4"
                      dateTransformation="endOfDay"
                    />
                  )}
                </Popover>
              )}
            />
          </div>
        </div>
      </form>
    </div>
  );
};

export default EditAchievementsFormContainer;
